import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Service } from '@/types/Service';
import { Edit, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ServiceCardProps {
  service: Service;
  onEdit: (service: Service) => void;
  onDelete: (serviceId: number) => void;
  onToggleEnabled: (serviceId: number, enabled: boolean) => Promise<void>;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  onEdit,
  onDelete,
  onToggleEnabled,
}) => {
  const { t } = useTranslation();

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} ${t('agent.services.duration.minutes.translation', 'min')}`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours} ${t('agent.services.duration.hours.translation', 'hr')}`;
    }
    return `${hours} ${t('agent.services.duration.hours.translation', 'hr')} ${mins} ${t('agent.services.duration.minutes.translation', 'min')}`;
  };

  // Three states, and the card has to tell them apart the same way the agent
  // does: 0 is free, null means no price is published, anything above 0 is the
  // price itself. Showing null as "Free" is what let a service the agent will
  // not quote a price for look like a free one.
  const formatPrice = (price: number | null) => {
    if (price === null)
      return t('agent.services.price.unlisted.translation', 'Price on request');
    if (price === 0) return t('agent.services.price.free.translation', 'Free');
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  return (
    <Card className="bg-card p-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-foreground">
              {service.name}
            </h3>
            {!service.enabled && (
              <Badge variant="secondary" className="text-xs">
                {t('agent.services.status.disabled.translation', 'Disabled')}
              </Badge>
            )}
          </div>
          {service.description && (
            <p className="mb-3 text-sm text-muted-foreground">
              {service.description}
            </p>
          )}
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="text-muted-foreground">
              {t('agent.services.duration.label.translation', 'Duration')}:{' '}
              <span className="font-medium text-foreground">
                {formatDuration(service.duration_minutes)}
              </span>
            </span>
            <span className="text-muted-foreground">
              {t('agent.services.price.label.translation', 'Price')}:{' '}
              <span className="font-medium text-foreground">
                {formatPrice(service.price)}
              </span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <Switch
              checked={service.enabled}
              onCheckedChange={checked => onToggleEnabled(service.id, checked)}
            />
            <span className="hidden text-xs text-muted-foreground sm:inline">
              {service.enabled
                ? t('agent.services.enabled.translation', 'Enabled')
                : t('agent.services.disabled.translation', 'Disabled')}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onEdit(service);
            }}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onDelete(service.id);
            }}
            className="h-8 w-8 p-0 text-red-500 hover:text-red-600 dark:hover:text-red-400"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
